import { Box, Link, Skeleton, Typography } from '@mui/material';
import { AddressElement } from '@stripe/react-stripe-js';
import { StripeAddressElementChangeEvent } from '@stripe/stripe-js';
import { Trans, useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useSearchParams } from 'react-router-dom';

import TextField from '../../../../../shared/components/TextField';
import TaxIdInput from '../TaxIdInput';
import useCountries from '../../../../../shared/features/countries/hooks/useCountries';
import rootStore from '../../../rootStore/RootStore';
import useStripeSubscriptionPreview from '../../../subscriptionPreview/hooks/useStripeSubscriptionPreview';
import { readPlanParam } from '../../../../../shared/helpers/functions/navigation';
import { Plan } from '../../../../../shared/helpers/constants/plans';
import useUser from '../../../../../shared/features/user/hooks/useUser';
import useSignOut from '../../../authentication/hooks/useSignOut';

const UserContacts = observer(() => {
  const { t } = useTranslation();
  const { generateSubscriptionPreview } = useStripeSubscriptionPreview();
  const { userStore, checkoutStore, uiStateStore } = rootStore;
  const { alpha2List, loaded: countriesLoaded } = useCountries(rootStore);
  const { isCombinedDataLoaded: userDataLoaded } = useUser(rootStore);
  const { signOut } = useSignOut();
  const userDataCountry = userStore.userDataCountryInfo;
  const [searchParams] = useSearchParams();
  const plan = readPlanParam(searchParams) as Plan;

  const handleAddressChange = (event: StripeAddressElementChangeEvent) => {
    const previewParams =
      plan === Plan.annual
        ? {
            annual: {
              area: uiStateStore.annualPlan.area,
              seats: uiStateStore.annualPlan.additionalSeats,
              planet: uiStateStore.annualPlan.planetImagery,
            },
          }
        : { monthly: true };

    userStore.setSelectedCountry(event.value.address.country);
    void generateSubscriptionPreview({
      ...previewParams,
      promocode: checkoutStore.promocode,
    });
  };

  if (!countriesLoaded || !userDataLoaded) {
    return <Skeleton variant="rounded" height={340} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="h4" sx={{ mb: 2 }}>
        {t('user-contacts.title')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <TextField
          disabled
          value={userStore.combinedData.userData?.email}
          label={t('user-contacts.email')}
        />
        <Typography variant="body2">
          <Trans
            i18nKey="user-contacts.change-account"
            components={{
              1: <Link component="button" onClick={signOut} />,
            }}
          />
        </Typography>
        <AddressElement
          onChange={handleAddressChange}
          options={{
            mode: 'billing',
            allowedCountries: alpha2List,
            display: { name: 'organization' },
            defaultValues: {
              address: {
                country: userDataCountry.alpha2,
              },
            },
          }}
        />
      </Box>
      <TaxIdInput />
    </Box>
  );
});

export default UserContacts;
